"use client";

import React from "react";
import { Bug, Target, Shield, BugOff, Droplets, Tractor, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "termite",
  title: "Arcadia Termite Control",
  description: <>
        Protect your rural properties, farmland, and ranches from destructive colonies with our specialized{" "}
        <Link to="/TermiteTreatmentPage" className="text-primary hover:underline">
          Arcadia termite treatments
        </Link>{" "}
        and agricultural solutions.
      </>,
  icon: Bug,
  link: "/TermiteTreatmentPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_termite_treatment_professional.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_termite_treatment_professional.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_termite_treatment_professional.png-wvc-sizes",
    alt: "Professional termite control services in Arcadia"
  }
}, {
  id: "ant",
  title: "Ant Eradication",
  description: "Comprehensive ant eradication for Arcadia farms, ranches, and outbuildings, ensuring these pests stay away from your property and livestock areas.",
  icon: Target,
  link: "/ArcadiaPestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ant_infestation_control.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ant_infestation_control.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ant_infestation_control.png-wvc-sizes",
    alt: "Targeted ant eradication services"
  }
}, {
  id: "roach",
  title: "Roach Extermination",
  description: "Fast and effective cockroach removal for Arcadia homes, farmhouses, and agricultural facilities to maintain a clean and sanitary environment.",
  icon: Shield,
  link: "/ArcadiaPestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_cockroach_extermination_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_cockroach_extermination_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_cockroach_extermination_service.png-wvc-sizes",
    alt: "Effective roach extermination treatments"
  }
}, {
  id: "spider",
  title: "Spider Management",
  description: "Manage spiders across rural lands and farm structures in Arcadia with targeted treatments for both indoor living spaces and outdoor work areas.",
  icon: BugOff,
  link: "/ArcadiaPestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_spider_web_management.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_spider_web_management.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_spider_web_management.png-wvc-sizes",
    alt: "Spider management and web removal"
  }
}, {
  id: "mosquito",
  title: "Mosquito Reduction",
  description: <>
        Reclaim your yards, fields, and livestock areas in Arcadia with our seasonal{" "}
        <Link to="/MosquitoControlPage" className="text-primary hover:underline">
          mosquito barrier treatments
        </Link>{" "}
        and breeding site reduction strategies.
      </>,
  icon: Droplets,
  link: "/MosquitoControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_mosquito_barrier_protection.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_mosquito_barrier_protection.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_mosquito_barrier_protection.png-wvc-sizes",
    alt: "Backyard mosquito reduction services"
  }
}, {
  id: "ranch",
  title: "Arcadia Pest Solutions for Families & Ranches",
  description: "Tailored pest solutions for families, ranches, and large agricultural properties in Arcadia, designed to handle the unique challenges of rural Florida living.",
  icon: Tractor,
  link: "/ArcadiaPestControlPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ranch_agricultural_pest_solutions.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ranch_agricultural_pest_solutions.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_ranch_agricultural_pest_solutions.png-wvc-sizes",
    alt: "Pest control for ranches and large properties"
  }
}];
export default function ArcadiaPestServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="arcadia-pest-services" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-6">
            Targeted Pest Control for Arcadia
          </h2>
          <p className="text-lg text-foreground/80 font-sans">
            From residential homes to sprawling ranches, we provide specialized pest management solutions designed for the unique environment of Arcadia.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
          const Icon = service.icon;
          return <div key={service.id} data-index={index} className={`transition-all duration-700 ease-out delay-${index * 100} ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                <Card className="h-full flex flex-col overflow-hidden border-border/10 shadow-md hover:shadow-xl transition-shadow duration-300 group">
                  <div className="overflow-hidden">
                    <AspectRatio ratio={16 / 9}>
                      <img src={service.image.src} data-wvc-srcset={service.image.srcset} data-wvc-sizes={service.image.sizes} alt={service.image.alt} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                    </AspectRatio>
                  </div>
                  
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-primary/10 rounded-md text-primary">
                        <Icon className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl font-default text-card-foreground">
                        {service.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    <CardDescription className="text-base text-muted-foreground font-sans">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                  
                  <CardFooter className="pt-4 border-t border-border/5">
                    <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-primary/5">
                      <Link to={service.link}>
                        <span className="font-default">Learn More</span>
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="font-default text-lg px-8">
            <Link to="/ContactPage">
              Schedule an Arcadia Inspection
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}